// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { LinkProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CategoryItem, { CategoryItemStyles } from "@sc/App/Components/Categories/CategoryItem";
import TitleTypography from "@sc/App/Components/Common/TitleTypography";
import XngCarousel, { SwiperStyle } from "@sc/App/Components/XngCarousel";
import loadFeaturedCategories from "@sc/App/Handlers/Categories/LoadFeaturedCategories";
import { CustomAppState } from "@sc/App/Store/Reducers";
import {
    FEATURED_CATEGORIES_CAROUSEL_SLIDES_PER_SCREENSIZE,
    SLIDES_PER_VIEW_POINTS,
    XNGAGE_CUSTOM_WIDGET,
} from "@sc/XDL/constants";
import { color, xScreen } from "@sc/XDL/Utilities";
import { generateSlidesPerViewMap } from "@sc/XDL/Utilities";
import * as React from "react";
import { useEffect, useRef } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    viewAllLink = "viewAllLink",
    selectedCategoryIds = "selectedCategoryIds",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: any[];
        [fields.viewAllLink]: LinkFieldValue;
        [fields.selectedCategoryIds]: string[];
    };
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const dataViewKey = getDataViewKey({ categoriesIds: ownProps.fields.selectedCategoryIds });
    const categories = state.xFeaturedCategories[dataViewKey]?.data;
    return {
        categories,
        isLoading: state.xFeaturedCategories.isLoading,
        isTabletScreen: state.xCommon.isTabletScreen,
    };
};
const mapDispatchToProps = {
    loadFeaturedCategories,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface FeaturedCategoriesStyles {
    link?: LinkProps;
    wrapper?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselExtendedStyles?: SwiperStyle;
    categoryItemStyles?: CategoryItemStyles;
}

export const featuredCategoriesStyles: FeaturedCategoriesStyles = {
    wrapper: {
        css: css`
            padding-bottom: 32px;
            ${xScreen("lte", "md")} {
                padding-bottom: 16px;
            }
        `,
    },
    carouselExtendedStyles: {
        container: {
            css: css`
                .xng-pagination-container > .swiper-scrollbar.swiper-scrollbar-horizontal {
                    background: ${color("common.ui1")};
                }
            `,
        },
    },
    categoryItemStyles: {
        image: {
            css: css`
                width: 204px;
                height: 204px;

                ${xScreen("gte", 1600)} {
                    width: 243px;
                    height: 243px;
                }

                object-fit: contain;
                justify-content: flex-start;
                img {
                    width: 100%;
                    height: auto;
                    max-height: 100%;
                }
            `,
        },
    },
};

const styles = featuredCategoriesStyles;

const FeaturedCategories: React.FC<Props> = ({
    fields: { title, viewAllLink, selectedCategoryIds },
    isLoading,
    categories,
    loadFeaturedCategories,
    isTabletScreen,
    zone,
}: Props) => {
    // XNG-CHANGE
    const dispatch = useDispatch();
    const isEnabled = useRef(false);

    useEffect(() => {
        loadFeaturedCategories({ categoriesIds: selectedCategoryIds });
    }, [selectedCategoryIds]);

    const viewAllLinkUrl = useGetLink(viewAllLink)?.url;

    useEffect(() => {
        if (categories?.length > 0 && zone.includes("tab") && !isEnabled.current) {
            isEnabled.current = true;
            dispatch({
                type: "xTabs/EnableZone",
                data: {
                    zone,
                    isEnabled: categories.length > 0, // we need to show this tab if we are in tab context
                },
            });
        }
    }, [categories]);

    if (!categories || !categories.length || isLoading) {
        return null;
    }

    const breakpoints = generateSlidesPerViewMap({
        [SLIDES_PER_VIEW_POINTS.xs]: FEATURED_CATEGORIES_CAROUSEL_SLIDES_PER_SCREENSIZE.mobile,
        [SLIDES_PER_VIEW_POINTS.sm]: FEATURED_CATEGORIES_CAROUSEL_SLIDES_PER_SCREENSIZE.tablet,
        [SLIDES_PER_VIEW_POINTS.md]: FEATURED_CATEGORIES_CAROUSEL_SLIDES_PER_SCREENSIZE.tablet,
        [SLIDES_PER_VIEW_POINTS.lg]: FEATURED_CATEGORIES_CAROUSEL_SLIDES_PER_SCREENSIZE.desktop,
        [SLIDES_PER_VIEW_POINTS.xl]: FEATURED_CATEGORIES_CAROUSEL_SLIDES_PER_SCREENSIZE.desktop,
    });

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="featuredCategoriesWidget">
            {title && (
                <TitleTypography
                    link={viewAllLinkUrl}
                    linkText={translate("View All")}
                    data-test-selector="featuredCategoriesTitle"
                >
                    {title}
                </TitleTypography>
            )}

            <StyledWrapper {...styles.carouselSlidesContainer}>
                <XngCarousel
                    spaceBetween={22}
                    scrollbar={{ draggable: true, el: ".swiper-scrollbar", enabled: isTabletScreen }}
                    navigation={false}
                    pagination={false}
                    breakpoints={breakpoints}
                    loop={!isTabletScreen}
                    centerInsufficientSlides={false}
                    autoplay={{
                        delay: 2500,
                        disableOnInteraction: false,
                        stopOnLastSlide: false,
                    }}
                    extendedStyles={styles.carouselExtendedStyles}
                >
                    {categories?.map((item: any) => (
                        <CategoryItem
                            key={item.id}
                            category={item}
                            categoryImage={item.largeImagePath}
                            extendedStyles={styles.categoryItemStyles}
                        />
                    ))}
                </XngCarousel>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(FeaturedCategories),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.viewAllLink,
                displayName: "View All Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Select Category",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
                validate: value => (value.length > 10 ? translate("You Can Add 10 Categories Only") : null),
            },
        ],
    },
};

export default widgetModule;
